import { Box, Checkbox, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormGroup, FormHelperText, InputLabel, Link, MenuItem, Select, Typography, Unstable_Grid2 as Grid } from "@mui/material"
import { useForm } from "react-hook-form"
import { LoadingButton } from "@/components/common/LoadingButton";
import { FormTextField } from "@/components/HookForm";
import { dialogClose } from "@/modules/MuiDialog";
import { toast } from "react-toastify";
import { patchMallBusinessInfo } from "@/services/mall.service";
import { banks } from "@/constant/banks";
import { useState } from "react";
import SquareThumbInput from "@/components/FileInput/SquareThumbInput";
import { uploadImageToStorage } from "@/services/proxy.service";
import DialogTitleBox from "@/modules/MuiDialog/DialogTitleBox";
import makeErrorString from "@/utils/makeErrorString";
import delay from "@/utils/delay";
import { postAuthAccount } from "@/services/partners.service";
import { useTranslation } from "react-i18next";

interface AccountInfoType {
    settleBank?: string;
    settleBankCode?: string;
    settleAccountNumber?: string;
    settleAccountOwnerName?: string;
    settleBankBookUrl?: string;
}
interface AccountInfoDialogProps extends AccountInfoType {
    mallId: string;
    onClose?: () => void;
    pageType?: 'mall' | 'partners';
    userId: string;
}

export const AccountInfoDialog = (props: AccountInfoDialogProps & Record<string, any>) => {
    const { pageType = 'mall', userId = '' } = props
    const { t } = useTranslation();

    const [bankBookFile, setBankBookFile] = useState<File | Blob | undefined>();

    const form = useForm<AccountInfoType>({
        defaultValues: {
            settleBank: props?.settleBank || '',
            settleBankCode: props?.settleBankCode || '',
            settleAccountNumber: props?.settleAccountNumber || '',
            settleAccountOwnerName: props?.settleAccountOwnerName || '',
            settleBankBookUrl: props?.settleBankBookUrl || ''
        }
    })
    const [isAuth, setIsAuth] = useState(true)
    const [isAuthLoading, setIsAuthLoading] = useState(false)
    const {
        register, watch,
        handleSubmit, trigger,
        formState: { isValid, isDirty, errors, isSubmitting, },
        setValue
    } = form;
    async function formSubmit(data: AccountInfoType) {
        const bankCode = banks.find(o => o.label === data.settleBank)
        if (!bankCode) {
            toast.error(t('mall.pleaseselectBankAgain'))
            return;
        }
        if (!isAuth) {
            toast.error(t('mall.pleaseAuthenticateAccount'))
            return;
        }
        if (pageType === 'mall' && !props?.settleBankBookUrl && !bankBookFile) {
            toast.error(t('mall.pleaseRegisterBankbookCopy'))
            return;
        }
        const settleBankBookUrl = !!bankBookFile ? await uploadImageToStorage([bankBookFile], 1500).then(r => {
            return !!r ? r.url[0] : '';
        }) : '';
        const { settleBankBookUrl: _, ...rest } = data
        const payload = !!bankBookFile
            ? { ...rest, settleBankCode: bankCode.code, settleBankBookUrl }
            : { ...rest, settleBankCode: bankCode.code, }
        try {
            await patchMallBusinessInfo(props.mallId, { ...payload })
            toast.success(t('common.updated'))
            dialogClose()
            if (!!props?.onClose) props.onClose()
        } catch (error) {
            toast.error(t('mall.accountInfoUpdateFailed'))
        }
    }
    async function authAccount() {
        try {
            const bankCode = banks.find(o => o.label === watch()?.settleBank || '')
            if (!(bankCode?.code)) {
                toast.error(t('mall.pleaseselectBankAgain'))
                return;
            }
            setIsAuthLoading(true)
            const check = await postAuthAccount({
                userId: userId,
                payload: {
                    settleBankCode: bankCode?.code || '',
                    settleBank: watch()?.settleBank || '',
                    settleAccountNumber: watch()?.settleAccountNumber || '',
                    settleAccountOwnerName: watch()?.settleAccountOwnerName || ''
                }
            })
            if (!!check) {
                setIsAuth(true)
                toast.success(t('mall.accountAuthenticationCompleted'))
                return;
            } else {
                throw new Error(t('mall.accountAuthenticationFailed'))
            }
        } catch (error) {
            toast.error(makeErrorString(error, t('mall.accountAuthenticationFailed')), { toastId: 'auth-account-error' })
            throw error
        } finally {
            setIsAuthLoading(false)
        }
    }
    return (
        <>
            {/* <DialogTitle>계좌정보 수정</DialogTitle> */}
            <DialogTitleBox title={t('mall.editAccountNumber')} />
            <DialogContent>
                <Grid container>
                    <Grid xs={12}>
                        <FormControl fullWidth error={!!errors.settleBank && !!errors.settleBank?.message}>
                            <InputLabel id="bank-selector-label">{t('mall.bank')}</InputLabel>
                            <Select
                                labelId={'bank-selector-label'}
                                id={'bank-selector'}
                                label={t('mall.bank')}
                                value={watch().settleBank}
                                {...register('settleBank', {
                                    validate: v => {
                                        if (!v || !v.trim()) return t('mall.selectBank')
                                        return true
                                    },
                                    onChange: async (e) => {
                                        await trigger('settleBank')
                                        setIsAuth(false)
                                    }
                                })}
                                onClose={async () => { await trigger('settleBank') }}
                            >
                                {banks.map(item => {
                                    return <MenuItem value={item.label} key={item.code}>{item.label}</MenuItem>
                                })}
                            </Select>
                            <FormHelperText>{!!errors.settleBank ? errors.settleBank?.message : ' '}</FormHelperText>
                        </FormControl>
                        <FormTextField
                            required
                            label={t('mall.accountHolder')}
                            name={'settleAccountOwnerName'}
                            form={form}
                            onChange={async () => {
                                setIsAuth(false)
                            }}
                            formTrigger={'change'}
                            defaultValue={''}
                            placeholder={t('mall.enterAccountHolder')}
                            fullWidth
                            rules={[
                                v => (!!v && !!v.trim()) || t('mall.accountHolderRequired')
                            ]}
                        />
                        <FormTextField
                            required
                            label={t('mall.accountNumber')}
                            onChange={async () => {
                                setIsAuth(false)
                            }}
                            name={'settleAccountNumber'}
                            form={form}
                            formTrigger={'change'}
                            defaultValue={''}
                            placeholder={t('mall.enterAccountNumberWithoutDash')}
                            fullWidth
                            rules={[
                                v => (!!v && !!v.trim()) || t('mall.accountNumberRequired'),
                            ]}
                        />
                        {pageType === 'mall' && (
                            <>
                                {props?.settleBankBookUrl && (
                                    <Link typography={'caption'} href={props?.settleBankBookUrl || '/'} target={'_blank'}>
                                        {t('mall.viewExistingBankbook')}
                                    </Link>
                                )}
                                <SquareThumbInput
                                    label={t('mall.bankbookCopy')}
                                    description={t('mall.bankbookCopyDescription')}
                                    required
                                    onChanged={(file) => {
                                        setBankBookFile(file);
                                        setValue('settleBankBookUrl', '', { shouldDirty: true })
                                        setIsAuth(false)
                                    }}
                                />
                            </>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <LoadingButton
                    variant="contained"
                    isLoading={isAuthLoading}
                    disabled={!!isAuth}
                    onClick={authAccount}
                >{t('mall.accountAuthentication')}</LoadingButton>
                <LoadingButton
                    variant="contained"
                    isLoading={isSubmitting}
                    disabled={!isAuth || !isDirty}
                    onClick={async () => { await handleSubmit(formSubmit)() }}
                >{t('common.edit')}</LoadingButton>
                <LoadingButton variant="outlined" color={'error'} onClick={() => { dialogClose() }}>{t('common.cancel')}</LoadingButton>
            </DialogActions>
        </>
    )
}